import merge from 'lodash/merge';
import { isMacOs, isIOS } from 'react-device-detect';
import type { PatternWallpaperSettings } from '../core/PatternWallpaper';
import { ASSETS_PATH_PREFIX } from '../helpers/electron';
import { getPatternUrlByTheme } from '../core/PatternWallpaper/helpers';
import { colors as colorsScheme } from './colors';
import type {
    DefaultThemeSettings,
    MergedThemeColorsScheme,
    ThemeAvatarkaColorKeys,
    ThemeInfoItem,
    ThemeMode,
    ThemeVariantKeys,
} from './types';

export const DEVICE_SIZES = {
    mobile600: {
        min: 0,
        max: 600,
    },
    mobile: {
        min: 472,
        max: 791,
    },
    tablet: {
        min: 792,
        max: 1111,
    },
    desktop: {
        min: 1112,
        max: 9999,
    },
    point488: {
        min: 488,
        max: 488,
    },
    point488_792: {
        min: 488,
        max: 792,
    },
    point792_1208: {
        min: 792,
        max: 1208,
    },
    point1208: {
        min: 1208,
        max: 9999,
    },
};

export const getAvailableThemeInfoList = () => {
    const themeInfoList: ThemeInfoItem[] = [];

    for (const key in colorsScheme) {
        if (colorsScheme.hasOwnProperty(key)) {
            const mode = key as ThemeMode;
            const variants = Object.keys(colorsScheme[mode].theme) as ThemeVariantKeys[];

            themeInfoList.push({
                mode,
                variants,
            });
        }
    }

    return themeInfoList;
};

const EXCLUDED_AVATARKA_KEYS: ThemeAvatarkaColorKeys[] = ['saved', 'deactivated'];

const getAvatarColorIndexByNumber = (id = 0, length = 0) => String(Math.abs(id) % length) as ThemeAvatarkaColorKeys;

const isNativeEmoji = isMacOs || isIOS;
const isCBDTEmoji = !isNativeEmoji && !(typeof CSS !== 'undefined' && CSS.supports('font-tech(color-COLRv1)'));

const getFontFamily = (): string => {
    const fontFamily = 'Roboto';
    if (isNativeEmoji) {
        return `${fontFamily}, -apple-system, apple color emoji, sans-serif`;
    }
    if (!isCBDTEmoji) {
        return `${fontFamily}, JoyPixelsSVG, sans-serif`;
    }

    return `${fontFamily}, JoyPixelsCBDT, sans-serif`;
};

export const createBaseTheme = (colors: MergedThemeColorsScheme) => ({
    breakpoints: DEVICE_SIZES,
    fontFamily: getFontFamily(),
    emoji: {
        isNativeEmoji,
        isCBDTEmoji,
    },
    root: {
        colors,
        button: {
            primary: {
                padding: '12px 24px',
                color: colors.basicColors.white['100%'],
                background: colors.primary.default,
                hoverBackground: colors.primary.hover,
                focusBackground: colors.primary.pressed,
                activeBackground: colors.primary.pressed,
                disabledBackground: colors.primary['40%'],
                border: 'none',
                borderRadius: '26px',
            },
            primaryWhite: {
                padding: '12px 24px',
                color: colors.basicColors.black['80%'],
                background: colors.basicColors.white['100%'],
                hoverBackground: colors.basicColors.white['100%'],
                focusBackground: colors.basicColors.white['100%'],
                activeBackground: colors.basicColors.white['100%'],
                disabledBackground: colors.basicColors.white['50%'],
                border: 'none',
                borderRadius: '26px',
            },
            withoutBg: {
                padding: '12px 24px',
                color: colors.accent.textOrIcon,
                background: 'transparent',
                hoverBackground: colors.primary['10%'],
                focusBackground: colors.primary['10%'],
                activeBackground: colors.primary['20%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '26px',
            },
            secondary: {
                padding: '12px 24px',
                color: colors.basicColors['100%'],
                background: colors.basicColors['6%'],
                hoverBackground: colors.basicColors['10%'],
                focusBackground: colors.basicColors['10%'],
                activeBackground: colors.basicColors['15%'],
                disabledBackground: colors.basicColors['4%'],
                border: 'none',
                borderRadius: '26px',
            },
            secondaryWithoutBg: {
                padding: '12px 24px',
                color: colors.basicColors['90%'],
                background: 'transparent',
                hoverBackground: colors.basicColors['10%'],
                focusBackground: colors.basicColors['10%'],
                activeBackground: colors.basicColors['15%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '26px',
            },
            secondaryTextGreen: {
                padding: '7px 16px',
                fontSize: '14px',
                lineHeight: '18px',
                color: colors.basicColors['90%'],
                background: colors.basicColors['6%'],
                hoverBackground: colors.basicColors['10%'],
                focusBackground: colors.basicColors['10%'],
                activeBackground: colors.basicColors['15%'],
                disabledBackground: colors.basicColors['4%'],
                border: 'none',
                borderRadius: '26px',
            },
            outlined: {
                padding: '6px 16px',
                color: colors.basicColors['90%'],
                background: 'transparent',
                hoverBackground: colors.primary['10%'],
                activeBackground: colors.primary['30%'],
                focusBackground: colors.primary['20%'],
                disabledBackground: 'transparent',
                border: `1px solid ${colors.primary.default}`,
                borderRadius: '12px',
            },
            grey: {
                padding: '12px 24px',
                color: colors.basicColors.white['100%'],
                background: colors.basicColors['6%'],
                hoverBackground: colors.basicColors['10%'],
                focusBackground: colors.basicColors['10%'],
                activeBackground: colors.basicColors['30%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '26px',
            },
            secondaryWithIcon: {
                padding: '12px 24px',
                color: colors.basicColors['90%'],
                background: colors.basicColors['6%'],
                hoverBackground: colors.basicColors['10%'],
                focusBackground: colors.basicColors['10%'],
                activeBackground: colors.basicColors['15%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '26px',
            },
            secondaryWithIconNoText: {
                padding: '12px 24px',
                color: colors.basicColors['90%'],
                background: colors.basicColors['6%'],
                hoverBackground: colors.basicColors['10%'],
                focusBackground: colors.basicColors['10%'],
                activeBackground: colors.basicColors['15%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '28px',
            },
            danger: {
                padding: '12px 24px',
                color: '#fff',
                background: colors.system.dangerDefault,
                hoverBackground: colors.system.dangerHover,
                focusBackground: colors.system.dangerFocused,
                activeBackground: colors.system.dangerFocused,
                disabledBackground: colors.system['danger40%'],
                border: 'none',
                borderRadius: '26px',
            },
            dangerS: {
                padding: '11px 16px',
                color: '#fff',
                background: colors.system.dangerDefault,
                hoverBackground: colors.system.dangerHover,
                focusBackground: colors.system.dangerFocused,
                activeBackground: colors.system.dangerFocused,
                disabledBackground: colors.system['danger40%'],
                border: 'none',
                borderRadius: '26px',
            },
            success: {
                padding: '12px 24px',
                color: '#fff',
                background: colors.system.successDefault,
                hoverBackground: colors.system.successHover,
                focusBackground: colors.system.successFocused,
                activeBackground: colors.system.successFocused,
                disabledBackground: colors.system['success40%'],
                border: 'none',
                borderRadius: '26px',
            },
            outlinedDanger: {
                padding: '12px 24px',
                color: colors.system.dangerDefault,
                background: 'transparent',
                hoverBackground: colors.system.dangerHover,
                focusBackground: colors.system.dangerFocused,
                activeBackground: colors.system['danger20%'],
                disabledBackground: colors.system['danger40%'],
                border: 'none',
                borderRadius: '26px',
            },
            primaryOutlined: {
                padding: '14px 24px',
                color: colors.accent.textOrIcon,
                background: 'transparent',
                hoverBackground: colors.primary['10%'],
                focusBackground: colors.primary['10%'],
                activeBackground: colors.primary['20%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '28px',
            },
            systemWithoutBg: {
                padding: '12px 24px',
                color: colors.system.dangerDefault,
                background: 'transparent',
                hoverBackground: colors.system['danger10%'],
                focusBackground: colors.system['danger10%'],
                activeBackground: colors.system['danger20%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '26px',
            },
            secondaryOutlined: {
                padding: '12px 24px',
                color: colors.basicColors['50%'],
                background: 'transparent',
                hoverBackground: colors.basicColors['10%'],
                focusBackground: colors.basicColors['10%'],
                activeBackground: colors.basicColors['15%'],
                disabledBackground: 'transparent',
                border: 'none',
                borderRadius: '26px',
            },
        },
    },
    overlay: {
        background: colors.basicColors.black['80%'],
        notTransparentBackground: colors.basicColors.black['100%'],
    },
    slider: {
        track: {
            default: {
                background: colors.basicColors['30%'],
            },
            disabled: {
                background: 'rgb(96, 97, 102)',
            },
        },
        indicator: {
            default: {
                background: colors.primary.default,
            },
            disabled: {
                background: 'rgb(96, 97, 102)',
                boxShadow: '',
            },
            hover: {
                boxShadow: colors.primary['20%'],
            },
            active: {
                boxShadow: colors.primary['50%'],
            },
            focus: {
                boxShadow: colors.primary['50%'],
            },
        },
    },
    sidebar: {
        important: {
            color: colors.accent.textOrIcon,
            icon: colors.accent.textOrIcon,
            badge: colors.basicColors['50%'],
            disabled: colors.basicColors['80%'],
        },
        default: {
            color: colors.basicColors['90%'],
            icon: colors.basicColors['55%'],
            badge: colors.basicColors['50%'],
            disabled: colors.basicColors['80%'],
        },
        second: {
            color: colors.basicColors['50%'],
            icon: colors.basicColors['55%'],
            badge: colors.basicColors['50%'],
            disabled: colors.basicColors['80%'],
        },
        danger: {
            color: colors.system.dangerDefault,
            icon: colors.system.dangerDefault,
            badge: colors.system.dangerDefault,
            disabled: colors.basicColors['80%'],
        },
    },
    badge: {
        red: {
            color: colors.accent.newBadge,
        },
        green: {
            color: colors.primary.default,
        },
        gray: {
            color: colors.basicColors['80%'],
        },
        muted: {
            color: colors.background.badgeMuted,
        },
        text: {
            color: colors.background.textCounter,
        },
        mutedText: {
            color: colors.background.textMutedCounter,
        },
    },
    avatar: {
        userAvatar: {
            onlineBadge: {
                color: colors.system.online,
                borderColor: colors.basicColors['8%'],
            },
        },
        workspaceAvatar: {
            onlineBadge: {
                color: colors.system.online,
                borderColor: '#17181d',
            },
        },
        workspaceAvatarCover: {
            color: 'rgba(0, 0, 0, 0.04)',
        },
    },
    link: {
        color: colors.accent.themeAccent,
    },
    getAvatarColorsById: (id?: number) => {
        const keys = Object.keys(colors.avatarka) as ThemeAvatarkaColorKeys[];
        const length = keys.filter(key => !EXCLUDED_AVATARKA_KEYS.includes(key)).length;
        const index = getAvatarColorIndexByNumber(id, length);

        return {
            background: `linear-gradient(${colors.avatarka[index]}, ${colors.avatarkaSecond[index]})`,
            color: colors.avatarka[index],
        };
    },
    getAvatarColorsByKey: (key: keyof MergedThemeColorsScheme['avatarka']) => ({
        background: `linear-gradient(${colors.avatarka[key]}, ${colors.avatarkaSecond[key]})`,
        color: colors.avatarka[key],
    }),
    getMeetPanelColorsById: () => ({
        background: `linear-gradient(90deg, ${colors.system.successHover} 0%, ${colors.theme.themeFirst.accent.vKSDefault} 100%)`,
    }),
});

export const createLightModeTheme = (colors: MergedThemeColorsScheme, variant: ThemeVariantKeys) => {
    const { startBackground, middleBackground, endBackground } = colors.background;

    const defaultLinearGradientColor = `linear-gradient(135deg, ${startBackground}, ${middleBackground}, ${endBackground})`;

    return {
        root: {
            input: {
                root: {
                    color: 'rgba(0, 0, 0, 0.88)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderColor: 'transparent',
                },
                focused: {
                    background: colors.basicColors['6%'],
                    borderColor: colors.basicColors['80%'],
                },
                disabled: {
                    background: 'rgba(0, 0, 0, 0.02)',
                },
                error: {
                    borderColor: '#E34646',
                },
                placeholder: {
                    color: 'rgba(0, 0, 0, 0.4)',
                },
                placeholderDisabled: {
                    color: 'rgba(0, 0, 0, 0.24)',
                },
                label: {
                    color: 'rgba(0, 0, 0, 0.56)',
                },
                successLabel: {
                    color: '#36AC50',
                },
            },
            body: {
                color: '#000',
                background: '#fff',
            },
        },
        logo: {
            main: `${ASSETS_PATH_PREFIX}logo-light.svg`,
            withText: `${ASSETS_PATH_PREFIX}logo-text-light.svg`,
            vertical: `${ASSETS_PATH_PREFIX}logo-vertical-light.svg`,
        },
        modal: {
            background: 'rgba(0, 0, 0, 0.08)',
            boxShadow: '0px 11px 16px rgba(0, 0, 0, 0.11)',
            bgColorWithoutOpacity: '#fff',
        },
        selector: {
            base: {
                background: 'rgba(0, 0, 0, 0.04)',
                color: 'rgba(0, 0, 0, 0.88)',
            },
            disabled: {
                background: 'rgba(0, 0, 0, 0.04)',
                color: 'rgba(0, 0, 0, 0.24)',
            },
            icon: {
                fill: 'rgba(0, 0, 0, 0.88)',
            },
            menu: {
                color: colors.basicColors.black['100%'],
                background: colors.basicColors.white['100%'],
                itemHover: colors.basicColors['10%'],
            },
        },
        label: {
            background: colors.basicColors.white['80%'],
        },
        chatItem: {
            heading: {
                color: colors.basicColors.black['100%'],
            },
            text: {
                color: colors.basicColors.black['50%'],
            },
        },
        message: {
            base: {
                default: {
                    groupStartBorderRadius: '16px 16px 16px 4px',
                    groupEndBorderRadius: '4px 16px 16px 16px',
                    groupedBorderRadius: '4px 16px 16px 4px',
                },
                doubleSide: {
                    groupStartBorderRadius: '16px 16px 4px 16px',
                    groupEndBorderRadius: '16px 4px 16px 16px',
                    groupedBorderRadius: '16px 4px 4px 16px',
                },
                backgroundColor: '#fff',
                borderRadius: '16px',
                groupStartBorderRadiusMobile: '20px 20px 20px 0',
                groupEndBorderRadiusMobile: '0 20px 20px 20px',
                groupedBorderRadiusMobile: '0 20px 20px 0',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                myMessageAccent: colors.background['01Base'],
                someoneMessageAccent: colors.background['01Base'],
            },
            baseMine: {
                backgroundColor: `linear-gradient(${colors.background.myMessageStartGradient}, ${colors.background.myMessageEndGradient})`,
            },
            baseSomeone: {
                backgroundColor: colors.background.someoneMessage,
                backdropFilter: 'none',
            },
            system: {
                backgroundColor: colors.background.systemMessage,
            },
        },
        demoBackground: {
            color: 'rgba(0, 0, 0, 0.08)',
        },
        background: {
            spaces: {
                sidebar: colors.background['01Base'],
            },
            sidebar: {
                background: colors.background['01Base'],
            },
            messageScreen: {
                defaultPattern: {
                    gradient: defaultLinearGradientColor,
                    imageLink: getPatternUrlByTheme('light', variant),
                    background: defaultLinearGradientColor,
                    imageColor: colors.background.startPattern,
                },
                getPatternSettingsByMode: (color?: string): PatternWallpaperSettings => ({
                    background: color,
                    imageColor: colors.background.startPattern,
                }),
            },
            messagePanel: {
                background: colors.background['01Base'],
            },
            panelOverInput: {
                background: 'rgba(245, 245, 245, 1.00)',
            },
            maintenanceMode: {
                background: `url('${ASSETS_PATH_PREFIX}images/technical_error.svg')`,
                imageBackground: colors.background['01Base'],
                imageBackground2: colors.primary['30%'],
            },
            themeModeCard: {
                backgroundColor: 'rgba(255, 255, 255, 1.00)',
                skeletonColor: 'rgba(0, 0, 0, 0.15)',
            },
        },
        header: {
            border: `1px solid ${colors.basicColors['8%']}`,
        },
    };
};

export const createDarkModeTheme = (colors: MergedThemeColorsScheme, variant: ThemeVariantKeys) => {
    const { startBackground, middleBackground, endBackground } = colors.background;

    const defaultLinearGradientColor = `linear-gradient(135deg, ${startBackground}, ${middleBackground}, ${endBackground})`;

    return {
        root: {
            input: {
                root: {
                    color: colors.basicColors['90%'],
                    backgroundColor: colors.basicColors['6%'],
                    borderColor: 'transparent',
                },
                focused: {
                    background: colors.basicColors['6%'],
                    borderColor: colors.basicColors['80%'],
                },
                disabled: {
                    background: colors.basicColors['10%'],
                },
                error: {
                    borderColor: colors.system.dangerDefault,
                },
                placeholder: {
                    color: colors.basicColors['30%'],
                },
                placeholderDisabled: {
                    color: '#515151',
                },
                label: {
                    color: colors.basicColors.white['50%'],
                },
                successLabel: {
                    color: '#36AC50',
                },
            },
            body: {
                background: colors.background['01Base'],
                color: colors.basicColors['90%'],
            },
        },
        logo: {
            main: `${ASSETS_PATH_PREFIX}logo-dark.svg`,
            withText: `${ASSETS_PATH_PREFIX}logo-text-dark.svg`,
            vertical: `${ASSETS_PATH_PREFIX}logo-vertical-dark.svg`,
        },
        modal: {
            background: colors.basicColors['8%'],
            boxShadow: `0px 2px 12px ${colors.basicColors.black['24%']}`,
            bgColorWithoutOpacity: colors.background.sheetOrModal,
        },
        selector: {
            base: {
                background: colors.basicColors['10%'],
                color: colors.basicColors.white['100%'],
            },
            disabled: {
                color: colors.basicColors.black['50%'],
                background: '#1F1F1F',
            },
            icon: {
                fill: colors.basicColors.white['50%'],
            },
            menu: {
                color: colors.basicColors.white['100%'],
                background: 'rgb(67, 68, 73)',
                itemHover: colors.basicColors['10%'],
            },
        },
        label: {
            background: colors.basicColors.black['80%'],
        },
        chatItem: {
            heading: {
                color: colors.basicColors.white['100%'],
            },
            text: {
                color: colors.basicColors.white['50%'],
            },
        },
        avatar: {
            workspaceAvatarCover: {
                color: '',
            },
        },
        message: {
            base: {
                default: {
                    groupStartBorderRadius: '16px 16px 16px 4px',
                    groupEndBorderRadius: '4px 16px 16px 16px',
                    groupedBorderRadius: '4px 16px 16px 4px',
                },
                doubleSide: {
                    groupStartBorderRadius: '16px 16px 4px 16px',
                    groupEndBorderRadius: '16px 4px 16px 16px',
                    groupedBorderRadius: '16px 4px 4px 16px',
                },
                backgroundColor: colors.accent.textOrIcon,
                borderRadius: '16px',
                groupStartBorderRadiusMobile: '16px 16px 4px 16px',
                groupEndBorderRadiusMobile: '16px 4px 16px 16px',
                groupedBorderRadiusMobile: '16px 4px 4px  16px',
                border: `1px solid ${colors.basicColors['8%']}`,
                myMessageAccent: colors.background['01Base'],
                someoneMessageAccent: colors.background['01Base'],
            },
            baseMine: {
                backgroundColor: `linear-gradient(${colors.background.myMessageStartGradient}, ${colors.background.myMessageEndGradient})`,
            },
            baseSomeone: {
                backgroundColor: colors.background.someoneMessage,
                backdropFilter: 'none',
            },
            system: {
                backgroundColor: colors.background.systemMessage,
            },
        },
        demoBackground: {
            color: colors.basicColors['4%'],
        },
        background: {
            spaces: {
                sidebar: colors.background['01Base'],
            },
            sidebar: {
                background: colors.background['01Base'],
            },
            messageScreen: {
                defaultPattern: {
                    gradient: defaultLinearGradientColor,
                    imageLink: getPatternUrlByTheme('dark', variant),
                    background: colors.background.startPattern,
                    imageColor: defaultLinearGradientColor,
                },
                getPatternSettingsByMode: (color?: string): PatternWallpaperSettings => ({
                    background: colors.background.startPattern,
                    imageColor: color,
                }),
            },
            messagePanel: {
                background: colors.background['01Base'],
            },
            panelOverInput: {
                background: 'rgba(20, 21, 22, 1.00)',
            },
            maintenanceMode: {
                background: `url('${ASSETS_PATH_PREFIX}images/technical_error.svg')`,
                imageBackground: colors.background['01Base'],
                imageBackground2: colors.primary['30%'],
            },
            themeModeCard: {
                backgroundColor: 'rgba(19, 19, 19, 1.00)',
                skeletonColor: 'rgba(255, 255, 255, 0.15)',
            },
        },
        header: {
            border: ``,
        },
    };
};

export const getTheme = (mode: ThemeMode, variant: ThemeVariantKeys) => {
    const colorsSchemeByMode = colorsScheme[mode];
    const { theme } = colorsSchemeByMode;

    if (variant in theme) {
        const overrideVariantColors = theme[variant];
        const colors: MergedThemeColorsScheme = merge({}, colorsSchemeByMode, overrideVariantColors);

        const baseTheme = createBaseTheme(colors);

        // Данный синтаксис необходим чтобы возвращался статический тип Theme, который необходим в расширении styled интерфейса DefaultTheme
        // В случае несоотвествия переменных (объеденения типов через | ) в доступных схемах модов, создается динамический тип, что при расширении интерфейса DefaultTheme, в декларации styled-components, вызовет ошибку.
        if (mode === 'light') {
            return merge(
                {
                    isLight: true,
                    isDark: false,
                },
                baseTheme,
                createLightModeTheme(colors, variant),
            );
        } else {
            return merge(
                {
                    isLight: false,
                    isDark: true,
                },
                baseTheme,
                createDarkModeTheme(colors, variant),
            );
        }
    }

    throw new Error(`Theme Error: Variant ${variant as string} was not found in the ${mode} mod file`);
};

// For other projects we use dark theme by default.
export const defaultThemeSettings: DefaultThemeSettings = {
    mode: 'dark',
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variant: getAvailableThemeInfoList().find(item => item.mode === 'dark')!.variants[0],
};

export default getTheme(defaultThemeSettings.mode, defaultThemeSettings.variant);
